import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { config, authEnabled } from '@/config';
import { listTenants, type TenantSummary } from '@/api/jobs';
import { getTenantOverride, setTenantOverride } from '@/api/tenant-override';

type Profile = Record<string, unknown> & { email?: string };

function parseGroups(claim: unknown): string[] {
  if (Array.isArray(claim)) return claim.map(String);
  if (typeof claim !== 'string' || claim.length === 0) return [];
  return claim.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
}

// Admin-only tenant switcher (Q-B10): the selection rides on API calls as
// X-Reframer-Tenant; the API enforces the admin group server-side.
function TenantSwitcher({ ownTenant }: { ownTenant: string }) {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [selected, setSelected] = useState(getTenantOverride() ?? ownTenant);

  useEffect(() => {
    listTenants()
      .then((r) => setTenants(r.tenants))
      .catch((e) => console.warn('tenant list unavailable:', e));
  }, []);

  if (!tenants) return null;

  const change = (tenant: string) => {
    setSelected(tenant);
    setTenantOverride(tenant === ownTenant ? null : tenant);
  };

  return (
    <select
      className="shell-tenant-switcher"
      title="Acting as tenant"
      value={selected}
      onChange={(e) => change(e.target.value)}
    >
      {tenants.map((t) => (
        <option key={t.tenantId} value={t.tenantId}>
          {t.tenantId === ownTenant ? `${t.tenantId} (yours)` : t.tenantId}
        </option>
      ))}
    </select>
  );
}

// Signs out of both the local session and the Hosted UI session; Cognito's
// discovery document has no end_session_endpoint, so the /logout URL is
// built from the configured domain. logout_uri must be on the client's
// LogoutURLs list (CognitoLogoutURLs stack parameter).
function UserMenu() {
  const auth = useAuth();
  const profile = auth.user?.profile as Profile | undefined;
  const tenant = (profile?.['custom:tenant_id'] as string | undefined) ?? '?';
  const isAdmin = parseGroups(profile?.['cognito:groups']).includes('admin');

  const handleSignOut = () => {
    setTenantOverride(null);
    void auth.removeUser();
    const { domain, clientId } = config.cognito;
    if (domain && clientId) {
      const logoutUri = encodeURIComponent(window.location.origin);
      window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
  };

  return (
    <div className="shell-user">
      {isAdmin && <TenantSwitcher ownTenant={tenant} />}
      <span className="shell-user-chip" title={profile?.email}>
        {profile?.email ?? 'signed in'} · {tenant}
      </span>
      <button className="shell-signout" onClick={handleSignOut}>
        Sign out
      </button>
    </div>
  );
}

export default function AppShell() {
  // /jobs/new and /jobs/:jobId/editor are under /jobs, so NavLink would
  // light Jobs too; Jobs stays active on the list and job pages only, New
  // lights on /jobs/new and Editor on a job's editor.
  const { pathname } = useLocation();
  const onNewJob = pathname.startsWith('/jobs/new');
  const onJobEditor = /^\/jobs\/[^/]+\/editor\/?$/.test(pathname);
  return (
    <div className="shell">
      <header className="shell-header">
        {/* The editor's brand (WorkflowControls): logo + 24px/500 title. */}
        <NavLink to="/jobs" className="shell-brand">
          <img src="/logo-dark.svg" alt="" />
          <span>Fireside Tools</span>
        </NavLink>
        <nav className="shell-nav">
          <NavLink to="/jobs" className={({ isActive }) => (isActive && !onNewJob && !onJobEditor ? 'active' : '')}>
            Jobs
          </NavLink>
          <NavLink to="/jobs/new" className={({ isActive }) => (isActive ? 'active' : '')}>
            New
          </NavLink>
          <NavLink to="/editor" className={({ isActive }) => (isActive || onJobEditor ? 'active' : '')}>
            Editor
          </NavLink>
          <NavLink to="/compare?mode=view" className={({ isActive }) => (isActive ? 'active' : '')}>
            View
          </NavLink>
        </nav>
        {authEnabled ? <UserMenu /> : <span className="shell-user-chip">auth disabled</span>}
      </header>
      <main className="shell-main">
        <Outlet />
      </main>
    </div>
  );
}
